import styles from './invoiceStatusField.module.css'
import { Button, Badge, Spinner, showErrorStrToast } from '../../../../../features/ui'
import { Labels } from '../../../../../features/data-types'
import { FiscalDocument, FiscalDocumentStatus } from '../../../../fiscal-documents'
import { useServer } from '../../../../../services/server'
import { useMemo, useState } from 'react'

type Props = {
  invoices: FiscalDocument[]
  status: FiscalDocumentStatus
  onRefresh: () => Promise<void>
  onRegenerate: (invoice: FiscalDocument) => void
  isAutoRefreshExhausted?: boolean
}

const sortByIssueDate = (invoices: FiscalDocument[]) =>
  [...invoices].sort((invoiceA, invoiceB) => invoiceA.issueDate.localeCompare(invoiceB.issueDate))

export const InvoiceStatusField = (props: Props) => {
  const { invoices, status, onRefresh, onRegenerate, isAutoRefreshExhausted = false } = props

  const [loading, setLoading] = useState(false)

  const { refreshFiscalDocumentStatus } = useServer()

  // La factura en vuelo es la que hay que consultar contra Facturante; el rechazo se muestra con la
  // última factura rechazada, aunque después ya se haya autorizado otra parcial.
  const pendingInvoice = useMemo(
    () => invoices.find(invoice => invoice.status === FiscalDocumentStatus.TO_AUTHORIZE),
    [invoices]
  )
  const rejectedInvoice = useMemo(() => {
    const rejectedInvoices = sortByIssueDate(
      invoices.filter(invoice => invoice.status === FiscalDocumentStatus.REJECTED)
    )
    return rejectedInvoices[rejectedInvoices.length - 1]
  }, [invoices])

  const badgeClass = useMemo(() =>
    status === FiscalDocumentStatus.AUTHORIZED ?
      styles.authorized :
      (status === FiscalDocumentStatus.REJECTED ?
        styles.rejected :
        (status === FiscalDocumentStatus.TO_AUTHORIZE ?
          styles.toAuthorize :
          (status === FiscalDocumentStatus.PARTIALLY_INVOICED ? styles.partiallyInvoiced : ''))
      )
    , [status])

  const onRefreshInvoiceStatus = async () => {
    setLoading(true)
    try {
      // El server solo consulta a Facturante si el comprobante sigue pendiente de autorización; si el
      // webhook/emit async ya lo cerró, devuelve el estado local. Después releemos documento y operación.
      if (pendingInvoice) {
        await refreshFiscalDocumentStatus(pendingInvoice.id)
      }
      await onRefresh()
    } catch (error) {
      showErrorStrToast(
        error instanceof Error ? error.message : 'No se pudo actualizar el estado de la factura.'
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className={styles.fiscalDocumentStatus}>
      <div className={styles.statusRow}>
        <Badge
          label={Labels.fiscalDocumentStatusLabel(status || FiscalDocumentStatus.PENDING)}
          className={badgeClass}
        />
        {pendingInvoice && (
          loading ? (
            <Spinner size="m" label="Refrescando" />
          ) : isAutoRefreshExhausted ? (
            <Button
              icon="arrow-clockwise"
              label="Refrescar Factura"
              kind="primary"
              small
              onClick={onRefreshInvoiceStatus}
            />
          ) : (
            <Spinner size="m" label="Autorizando" />
          )
        )}
        {rejectedInvoice && (
          loading ? (
            <Spinner size="m" label="Refrescando" />
          ) : (
            <Button
              icon="file-earmark-check"
              label="Regenerar Factura"
              kind="quinary"
              small
              onClick={() => onRegenerate(rejectedInvoice)}
            />
          )
        )}
      </div>
      {rejectedInvoice?.statusMessage && (
        <span className={styles.message}>{rejectedInvoice.statusMessage}</span>
      )}
    </div>
  )
}
